/**
 * Sunucunun (server/seo/render.ts) bastığı JSON-LD ile istemci şemasının çakışmasını önler.
 * SSR her şemayı <script type="application/ld+json" data-dj-ssr="<yol>"> olarak basar. İlk yüklemede
 * aynı yol için SSR'de bulunan türler (BlogPosting, BreadcrumbList, FAQPage, Service…) istemcide ikinci
 * kez eklenmez; SPA geçişinde SSR şemaları eski sayfaya ait olduğundan silinir ve istemci şemaları eklenir.
 * Saf fonksiyonlar; test: server/seo/ssr_sema.test.ts.
 */
type Dugum = Record<string, unknown>;

const turleri = (d: unknown): string[] => {
  if (!d || typeof d !== "object") return [];
  const t = (d as Dugum)["@type"];
  return (Array.isArray(t) ? t : [t]).filter((x): x is string => typeof x === "string");
};

/** JSON-LD metinlerindeki @type değerleri (kök, dizi ve @graph düğümleri). Bozuk JSON yok sayılır. */
export function semaTurleri(metinler: string[]): Set<string> {
  const out = new Set<string>();
  for (const m of metinler) {
    let o: unknown;
    try {
      o = JSON.parse(m);
    } catch {
      continue;
    }
    for (const kok of Array.isArray(o) ? o : [o]) {
      turleri(kok).forEach(t => out.add(t));
      const g = kok && typeof kok === "object" ? (kok as Dugum)["@graph"] : undefined;
      if (Array.isArray(g)) g.forEach(d => turleri(d).forEach(t => out.add(t)));
    }
  }
  return out;
}

/**
 * SEO.tsx'in yoldan ürettiği genel BreadcrumbList eklenmeli mi? Kök yolda, SSR bu yol için BreadcrumbList
 * bastıysa (ilk yükleme) ya da sayfanın kendi şemasında BreadcrumbList varsa (SPA geçişi) eklenmez; böylece
 * sayfada tek BreadcrumbList kalır.
 */
export function genelBreadcrumbGerekli(yolParcaSayisi: number, ssrTurleri: ReadonlySet<string>, sema?: Dugum | null): boolean {
  if (yolParcaSayisi === 0 || ssrTurleri.has("BreadcrumbList")) return false;
  return !(sema && semaTurleri([JSON.stringify(sema)]).has("BreadcrumbList"));
}

/** İstemci şemasından SSR'de zaten bulunan türleri çıkarır; geriye düğüm kalmazsa null. */
export function semaSuz(sema: Dugum, ssrTurleri: ReadonlySet<string>): Dugum | null {
  if (!ssrTurleri.size) return sema;
  const ssrdeVar = (d: unknown) => turleri(d).some(t => ssrTurleri.has(t));
  const g = sema["@graph"];
  if (Array.isArray(g)) {
    const kalan = g.filter(d => !ssrdeVar(d));
    return kalan.length ? { ...sema, "@graph": kalan } : null;
  }
  return ssrdeVar(sema) ? null : sema;
}
